/**
 * =============================================================================
 * useIsMobile HOOK (P2-19)
 * =============================================================================
 *
 * Shared hook for detecting mobile viewport.
 * Uses centralized breakpoint constants to prevent CSS/JS drift.
 *
 * @example
 * ```tsx
 * const isMobile = useIsMobile();
 * return isMobile ? <MobileLayout /> : <DesktopLayout />;
 * ```
 *
 * =============================================================================
 */

import { useState, useEffect } from 'react';
import { BREAKPOINTS } from '../constants/breakpoints';

/**
 * Hook that returns true when viewport is below mobile breakpoint.
 *
 * @param breakpoint - Optional custom breakpoint (default: BREAKPOINTS.MOBILE)
 * @returns true if viewport width is below the breakpoint
 */
export function useIsMobile(breakpoint: number = BREAKPOINTS.MOBILE): boolean {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);

    const handler = (event: MediaQueryListEvent): void => {
      setIsMobile(event.matches);
    };

    // Set initial value
    setIsMobile(mediaQuery.matches);

    // Listen for changes
    mediaQuery.addEventListener('change', handler);

    return () => {
      mediaQuery.removeEventListener('change', handler);
    };
  }, [breakpoint]);

  return isMobile;
}
